/**
 * Network client: JSON control messages + binary state decoding.
 */

import { MAP_H, MAP_W, Msg, RES_COUNT, TILE_COUNT, UNIT_FLAG_CARGO } from '../shared/constants';

/**
 * How many times a join may be redirected before it settles for what it has.
 *
 * One per core plus a few: on Linux the cores are handed connections in turn,
 * so this many attempts walk right round the machine even if the first one
 * starts in the worst place. The spare few cover a core restarting mid-walk.
 */
const MAX_JOIN_TRIES = 18;
import { BinReader, ClientMsg, PlayerInfo, ServerMsg, unpackPos } from '../shared/protocol';
import { ON_CRAZYGAMES, wsUrl } from './platform';
import { applyOwner, markDirty, NetPlayer, resetWorld, state } from './state';

/** What the create form chose, when the player is opening a world of their own. */
export interface RoomOpts {
  mode: number;
  /** the Light edition — a world you open yourself is the one you are playing */
  light: boolean;
  maxPlayers: number;
  teams: number;
  aiKingdoms: number;
  camps: number;
}

/**
 * **A random string this browser keeps, and the only key to a sleeping kingdom.**
 *
 * Reconnection used to match on the player's *name*, which is neither secret
 * nor unique — anybody could read one off the map, type it, and walk into that
 * realm. This is not an account and says nothing about who you are; it says
 * only that this is the same browser as a minute ago, which is all reconnecting
 * needs. It never leaves the machine except as this opaque string, and clearing
 * site data simply means the next match starts fresh.
 */
function browserToken(): string {
  let t = '';
  try {
    t = localStorage.getItem('kaelmoor.token') || '';
    if (!t) {
      t = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      localStorage.setItem('kaelmoor.token', t);
    }
  } catch {
    // storage refused (private mode, blocked cookies): no token, no reconnect,
    // which is a worse experience and still a correct one
  }
  return t;
}

type Handlers = {
  onLobby?: (msg: any) => void;
  onMatch?: (msg: any) => void;
  onPhase?: (phase: number, countdown: number) => void;
  onNotice?: (text: string, kind?: string) => void;
  onPing?: (msg: any) => void;
  onGameOver?: (msg: any) => void;
  onEffect?: (type: number, x: number, y: number, x2: number, y2: number, param: number) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

class Net {
  ws: WebSocket | null = null;
  handlers: Handlers = {};
  rtt = 0;
  private lastPing = 0;

  /**
   * How many times a join has bounced off the wrong core.
   *
   * A busy server is several processes sharing one port, and the operating
   * system decides which one answers. Two things bounce off that: a **code**
   * belonging to core 2 that lands on core 0, and a **quickplay** that core 0
   * wants to send to whichever core's lobby is filling up. Either way we hang
   * up and dial again, and the next connection is spread somewhere else — on
   * Linux the cores are handed connections in turn, so a few tries walk right
   * round them. The player sees nothing but a slightly slower join.
   */
  private roomRetries = 0;
  private lastJoin: { name: string; room?: string; create: boolean; opts?: RoomOpts } | null = null;

  connect(name: string, room?: string, create = false, opts?: RoomOpts) {
    /*
     * Not `location.host`. On kaelmoor.io the page and the war come off the same
     * process and the two are identical, but a portal build is served from the
     * portal's CDN — deriving the socket from the page there would open it
     * against a domain with no game server behind it. See `client/platform.ts`.
     */
    const url = wsUrl();
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    this.lastJoin = { name, room, create, opts };

    ws.onopen = () => {
      state.connected = true;
      // the settings only travel when you are opening a world; the server
      // clamps every one of them anyway (`roomConfigFrom`)
      this.send({
        t: 'hello', name, room, create, race: state.race, version: 1, opts,
        // which edition Quickplay should look in when no code was given
        light: state.light,
        // the one thing that can hand a sleeping kingdom back — see `browserToken`
        token: browserToken(),
        /*
         * Which front door this player came through, so the funnel can tell a
         * portal's traffic from our own. Sent only when there is something to
         * say: the web build omits the field entirely and the server reads the
         * absence as `web`, which keeps the site's messages exactly as they
         * were. Not an identifier — it is the same string for everybody in this
         * build.
         */
        ...(ON_CRAZYGAMES ? { src: 'crazygames' } : {}),
      });
      this.handlers.onOpen?.();
    };
    ws.onclose = () => {
      state.connected = false;
      this.handlers.onClose?.();
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') this.handleJson(JSON.parse(ev.data));
      else this.handleBinary(ev.data as ArrayBuffer);
    };
  }

  /** Drop the socket without telling the UI — used between join attempts. */
  private disconnectQuietly() {
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      try { ws.close(); } catch { /* already gone */ }
    }
    state.connected = false;
  }

  /** Leave the world on purpose (the defeat screen's "back to the menu"). */
  disconnect() {
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      try { ws.close(); } catch { /* already gone */ }
    }
    state.connected = false;
    this.handlers.onClose?.();
  }

  send(msg: ClientMsg) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  heartbeat() {
    const now = performance.now();
    if (now - this.lastPing < 3000) return;
    this.lastPing = now;
    this.send({ t: 'pong', ts: now });
  }

  private setPlayers(list: PlayerInfo[]) {
    const seen = new Set<number>();
    for (const info of list) {
      seen.add(info.id);
      let p = state.players.get(info.id);
      if (!p) {
        p = {
          id: info.id, name: info.name, team: info.team, color: info.color, bot: info.bot,
          kind: info.kind ?? 0, race: info.race ?? 0,
          tiles: 0, troops: 0, troopCap: 0, troopRate: 0, wallCount: 0,
          eliminated: false, connected: true, protectedNow: false,
          sumX: 0, sumY: 0, count: 0,
        };
        state.players.set(info.id, p);
      } else {
        p.name = info.name;
        p.team = info.team;
        p.color = info.color;
        p.bot = info.bot;
        p.kind = info.kind ?? 0;
        p.race = info.race ?? 0;
      }
      if (info.id === state.you) state.race = info.race ?? state.race;
    }
    for (const id of [...state.players.keys()]) if (!seen.has(id)) state.players.delete(id);
    state.fullRedraw = true;
  }

  private handleJson(msg: ServerMsg | any) {
    switch (msg.t) {
      /*
       * The core that answered does not own that room — dial again.
       *
       * Hanging up and reconnecting is the whole mechanism: the operating
       * system spreads each new connection across the worker processes, so a
       * fresh attempt lands somewhere else. A handful of tries covers any
       * realistic number of cores; after that we stop rather than loop, and
       * the player is told plainly instead of watching a spinner.
       */
      case 'retry-room': {
        const join = this.lastJoin;
        this.disconnectQuietly();
        if (!join || this.roomRetries >= MAX_JOIN_TRIES) {
          this.roomRetries = 0;
          this.handlers.onNotice?.(`No world answered to the code ${msg.room}. `
            + 'Check it and try again.', 'bad');
          this.handlers.onClose?.();
          return;
        }
        this.roomRetries++;
        /*
         * Dial the world we were *pointed at*, not the one we asked for.
         *
         * For a code join those are the same thing. For a quickplay being
         * steered they are not, and using the code is what makes the retry
         * converge: without it every attempt is a fresh "put me anywhere",
         * lands on another random core, and gets steered again — a coin flip
         * repeated rather than a walk round the machine.
         */
        const target = msg.room || join.room;
        setTimeout(() => this.connect(join.name, target, join.create, join.opts), 60);
        return;
      }
      case 'welcome':
        this.roomRetries = 0;
        state.you = msg.you;
        state.roomId = msg.room;
        /*
         * The way back, if the tab closes or the connection drops.
         *
         * Wars in progress are not listed in the browser — they are nobody
         * else's business and cannot be joined — so without this the one person
         * who *should* get back in would have nothing to click. Only this
         * browser knows the code, which is exactly the right audience.
         */
        try { localStorage.setItem('kaelmoor.room', msg.room); } catch { /* private mode */ }
        break;
      case 'lobby':
        state.phase = msg.phase;
        state.countdown = msg.countdown;
        if (msg.info) state.lobby = msg.info;
        this.setPlayers(msg.players);
        this.handlers.onLobby?.(msg);
        break;
      case 'match':
        state.seed = msg.seed;
        // the world's shape travels with its seed; older servers sent neither
        state.worldW = msg.worldW || MAP_W;
        state.worldH = msg.worldH || MAP_H;
        state.phase = msg.phase;
        state.countdown = msg.countdown;
        resetWorld();
        this.setPlayers(msg.players);
        this.handlers.onMatch?.(msg);
        break;
      case 'phase':
        state.phase = msg.phase;
        state.countdown = msg.countdown;
        this.handlers.onPhase?.(msg.phase, msg.countdown);
        break;
      case 'notice':
        this.handlers.onNotice?.(msg.text, msg.kind);
        break;
      case 'ping':
        state.pings.push({ x: msg.x, y: msg.y, kind: msg.kind, color: msg.color, born: performance.now() });
        this.handlers.onPing?.(msg);
        break;
      case 'war':
        state.atWar = new Set(msg.ids);
        break;
      case 'gameover':
        state.gameOver = { standings: msg.standings, winner: msg.winner };
        this.handlers.onGameOver?.(msg);
        break;
      case 'ping-rtt':
        this.rtt = performance.now() - msg.ts;
        break;
    }
  }

  private handleBinary(buf: ArrayBuffer) {
    const r = new BinReader(buf);
    const type = r.u8();
    switch (type) {
      case Msg.TerritoryFull: {
        r.u32();
        const runs = r.varint();
        let cursor = 0;
        state.owner.fill(0);
        for (let i = 0; i < runs; i++) {
          const len = r.varint();
          const owner = r.u16();
          if (owner !== 0) {
            for (let k = 0; k < len; k++) state.owner[cursor + k] = owner;
          }
          cursor += len;
        }
        this.recountTerritory();
        state.fullRedraw = true;
        break;
      }
      case Msg.TerritoryDelta: {
        r.u32();
        const runs = r.varint();
        let cursor = 0;
        for (let i = 0; i < runs; i++) {
          const gap = r.varint();
          const len = r.varint();
          const owner = r.u16();
          const start = cursor + gap;
          for (let k = 0; k < len; k++) applyOwner(start + k, owner);
          cursor = start + len;
        }
        break;
      }
      /*
       * The standings, then stats for the kingdoms on this screen.
       *
       * The rows are no longer every kingdom in the world — see
       * `encodePlayers`. What the rows *cannot* answer, because it is a share
       * of the whole map rather than of the view, is who is winning; so that
       * arrives first, already totalled by the server that judges it.
       */
      case Msg.Players: {
        r.u32();
        state.contestedLand = r.varint();
        state.teamLand.clear();
        const teamCount = r.u8();
        for (let i = 0; i < teamCount; i++) {
          const team = r.u8();
          state.teamLand.set(team, r.varint());
        }
        const count = r.u16();
        for (let i = 0; i < count; i++) {
          const id = r.u16();
          const flags = r.u8();
          const tiles = r.varint();
          const troops = r.varint();
          const cap = r.varint();
          const rate = r.varint() / 100;
          const walls = r.varint();
          const p = state.players.get(id);
          if (!p) continue;
          p.wallCount = walls;
          p.eliminated = (flags & 1) !== 0;
          p.connected = (flags & 2) === 0;
          p.protectedNow = (flags & 4) !== 0;
          p.tiles = tiles;
          p.troops = troops;
          p.troopCap = cap;
          p.troopRate = rate;
          // your own kingdom falling is a private event: only you get the screen
          if (id === state.you && p.eliminated && !state.defeated) state.defeated = true;
        }
        break;
      }
      case Msg.Entities: {
        r.u32();
        const selfId = r.u16();
        if (selfId !== 0) {
          state.self.troops = r.f32();
          state.self.committed = r.f32();
          state.self.cap = r.f32();
          for (let i = 0; i < RES_COUNT; i++) state.self.res[i] = r.f32();
          for (let i = 0; i < RES_COUNT; i++) state.self.income[i] = r.f32();
          state.self.starving = r.u8() === 1;
          state.self.troopRate = r.f32();
          state.self.villageBonus = r.f32();
          state.self.foodFactor = r.f32();
          state.self.armyCap = r.u8();
          state.self.wallCount = r.varint();
          const orders = r.u8();
          state.self.repairing = (orders & 1) !== 0;
          state.self.defending = (orders & 2) !== 0;
          state.self.powerUses = r.varint();
          state.self.centroidX = r.f32();
          state.self.centroidY = r.f32();
        }

        const now = performance.now();
        if (state.lastEntityAt > 0) {
          const dt = now - state.lastEntityAt;
          if (dt > 30 && dt < 400) state.tickInterval = state.tickInterval * 0.85 + dt * 0.15;
        }
        state.lastEntityAt = now;
        state.stamp++;

        const uCount = r.u16();
        for (let i = 0; i < uCount; i++) {
          const id = r.varint();
          const type = r.u8();
          const owner = r.u16();
          const x = unpackPos(r.u16());
          const y = unpackPos(r.u16());
          const hp = r.u8();
          const flags = r.u8();
          // a transport you are entitled to see inside carries its passenger count
          const cargo = (flags & UNIT_FLAG_CARGO) !== 0 ? r.varint() : 0;
          let u = state.units.get(id);
          if (!u) {
            u = { id, type, owner, px: x, py: y, tx: x, ty: y, t0: now, hp, flags, cargo, stamp: state.stamp };
            state.units.set(id, u);
          } else {
            u.cargo = cargo;
            const t = Math.min(1, (now - u.t0) / state.tickInterval);
            u.px = u.px + (u.tx - u.px) * t;
            u.py = u.py + (u.ty - u.py) * t;
            u.tx = x; u.ty = y; u.t0 = now;
            u.hp = hp; u.flags = flags; u.owner = owner; u.type = type;
            u.stamp = state.stamp;
          }
        }
        for (const [id, u] of state.units) {
          if (u.stamp !== state.stamp) { state.units.delete(id); state.selected.delete(id); }
        }

        // structures only arrive at 2 Hz; skip the sweep on ticks without them
        if (r.u8() === 1) {
          const sCount = r.u16();
          for (let i = 0; i < sCount; i++) {
            const id = r.varint();
            const type = r.u8();
            const owner = r.u16();
            const tile = r.varint();
            const hp = r.u8();
            const level = r.u8();
            const built = r.u8() / 255;
            const queue = r.u8();
            const jobs: { type: number; progress: number }[] = [];
            for (let q = 0; q < queue; q++) {
              jobs.push({ type: r.u8(), progress: r.u8() / 255 });
            }
            let s = state.structures.get(id);
            if (!s) {
              s = {
                id, type, owner, tile, x: tile % MAP_W, y: (tile / MAP_W) | 0,
                hp, queue, level, built, jobs, stamp: state.stamp,
              };
              state.structures.set(id, s);
            } else {
              s.type = type; s.owner = owner; s.tile = tile; s.hp = hp; s.queue = queue; s.level = level;
              s.built = built;
              s.jobs = jobs;
              s.stamp = state.stamp;
            }
          }
          for (const [id, s] of state.structures) {
            if (s.stamp !== state.stamp) state.structures.delete(id);
          }

          /*
           * The wall layer, as a sorted delta-coded run of tile indices. The
           * server sends the whole set in view every time, so it is replaced
           * rather than merged — a segment that has been knocked down is gone
           * by virtue of not arriving.
           */
          const wCount = r.u16();
          state.walls.clear();
          let wallCursor = 0;
          for (let i = 0; i < wCount; i++) {
            wallCursor += r.varint();
            const kind = r.u8();
            const owner = r.u16();
            const hp = r.u8();
            state.walls.set(wallCursor, { tile: wallCursor, kind, owner, hp });
          }
          state.wallRevision++;

          // allied books — only ever sent for kingdoms you are actually allied to
          const aCount = r.u8();
          state.allyBooks.clear();
          for (let i = 0; i < aCount; i++) {
            const id = r.u16();
            const res: number[] = [];
            for (let k = 0; k < RES_COUNT; k++) res.push(r.f32());
            state.allyBooks.set(id, { res, food: r.f32(), markets: r.u8() });
          }

          // every attack you have running, wilderness included
          const warCount = r.u8();
          state.attacks.length = 0;
          for (let i = 0; i < warCount; i++) {
            const target = r.u16();
            const troops = r.varint();
            state.attacks.push({ target, troops });
          }

          // doomsday weapons currently falling on somebody
          const pCount = r.u8();
          state.powers.length = 0;
          for (let i = 0; i < pCount; i++) {
            const x = unpackPos(r.u16());
            const y = unpackPos(r.u16());
            const owner = r.u16();
            const realm = r.u16();
            const area = r.u8();
            state.powers.push({ x, y, owner, realm, area });
          }

          // contested borders in view
          const fCount = r.u8();
          state.fronts.length = 0;
          for (let i = 0; i < fCount; i++) {
            const x = unpackPos(r.u16());
            const y = unpackPos(r.u16());
            const attacker = r.u16();
            const defender = r.u16();
            const atkTroops = r.varint();
            const defTroops = r.varint();
            const heading = (r.u8() / 255) * Math.PI * 2 - Math.PI;
            state.fronts.push({ x, y, attacker, defender, atkTroops, defTroops, heading });
          }
        }

        const eCount = r.u16();
        for (let i = 0; i < eCount; i++) {
          const ftype = r.u8();
          const x = unpackPos(r.u16());
          const y = unpackPos(r.u16());
          const x2 = unpackPos(r.u16());
          const y2 = unpackPos(r.u16());
          const param = r.u8();
          this.handlers.onEffect?.(ftype, x, y, x2, y2, param);
        }
        break;
      }
    }
  }

  private recountTerritory() {
    for (const p of state.players.values()) { p.count = 0; p.sumX = 0; p.sumY = 0; }
    for (let i = 0; i < TILE_COUNT; i++) {
      const o = state.owner[i];
      if (o === 0) continue;
      const p = state.players.get(o);
      if (!p) continue;
      p.count++;
      p.sumX += i % MAP_W;
      p.sumY += (i / MAP_W) | 0;
    }
  }
}

export const net = new Net();
